import { ChangeEvent, DragEvent, ForwardedRef, useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { Typography } from "@kaltura/ds-react-components";
import { getComponentFromPath, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import UploadService from "./upload/UploadService";
import { MediaUploadFileInfo } from "./upload/UploadMediaProps";
import { FileMetaData, StatusesMapper, UploadServiceHandler, UploadStatus } from "./upload/UploadServiceProps";
import { StyledBackLink, StyledPageBox } from "./StyledComponents";
import DragAndDrop from "./components/DragAndDrop/DragAndDrop";
import FileRows from "./fileRows/FileRows";
import Status from "./Status/Status";
import { SaveEditFormResponse, EntryMetadataStructure } from "@kaltura/mediaspace-shared-edit-entry";
import { KalturaMediaEntry } from "kaltura-typescript-client/api/types/KalturaMediaEntry";
import BulkEditModal from "./components/bulk-edit-modal/BulkEditModal";
import { QueryParams } from "@kaltura/mediaspace-shared-types";
import { UploadMediaBaseProps } from "./UploadMediaBaseProps";
import { ConfirmationModal, ConfirmationModalData, ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { PreSaveComponent } from "./components/PreSaveComponent/PreSaveComponent";
import { PostSaveComponent } from "./components/PostSaveComponent/PostSaveComponent";

const megaByteInKiloByteOnBinary = 1000;

export interface UploadMediaProps extends UploadMediaBaseProps {
    className?: string;
    createMedia: (query: QueryParams) => Promise<any>;
    deleteMedia: (query: QueryParams) => Promise<any>;
    checkMediaStatus: (query: QueryParams) => Promise<any>;
}

/**
 * UploadMedia Main Component
 */
export const UploadMedia = ({
    className,
    ks,
    serviceUrl,
    maxConcurrentUploadConnections,
    createMedia,
    deleteMedia,
    checkMediaStatus,
    chunkSize = megaByteInKiloByteOnBinary * 100, //100Kb - it's the default chunk size in UploadToken API
    filesExts,
    editForm,
    editFormComponents,
    publishEnabled = false,
    formId,
    showBackButton = false,
    showTitle = true,
    singleUpload = false,
    showEditFormButtons = true, // defaults true for backward compatability
    showCancelButton,
    showMediaLink = true, // defaults true for backward compatability
    saveButtonLabel,
    allowSaveUntouched,
    onEntryCreated,
    onEntryFinishedUploading,
    onEntryReady,
    onCanSaveForm,
    onEditFormShown,
    onReadyForPublish,
    onUploadCancelled,
    onSubmitStarted,
    onSubmit,
    preSaveComponent,
    postSaveComponent,
}: UploadMediaProps) => {
    const theme = useTheme();
    const isSmScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    const [modalData, setModalData] = useState<ConfirmationModalData>();
    const [filesInfo, setFilesInfo] = useState<MediaUploadFileInfo[]>([]);
    const [fileInfoToUpload, setFileInfoToUpload] = useState<MediaUploadFileInfo>();
    const [entriesIdToCheckStatus, setEntriesIdToCheckStatus] = useState<Set<string>>(new Set<string>());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);

    // used to remount FileRow actions and refresh props (publish) after bulk updates (edit/publish)
    const [bulkUpdatesCount, setBulkUpdatesCount] = useState(0);
    // used for cleanup
    const filesInfoRef = useRef(filesInfo);
    const cancelRef = useRef(false);

    const [formData, setFormData] = useState<{ entryIds: string[]; data: any }>({ entryIds: [], data: {} });
    const [displayPreSaveComponent, setDisplayPreSaveComponent] = useState<boolean>(false);
    const [displayPostSaveComponent, setDisplayPostSaveComponent] = useState<boolean>(false);
    const [saveConfirmationResolvePromise, setSaveConfirmationResolvePromise] = useState<
        ((value: { confirm: boolean; isEditable: boolean }) => void) | null
    >(null);

    const uploadServiceRef: ForwardedRef<UploadServiceHandler> = useRef<UploadServiceHandler>(null);

    const { showToast } = useToastsContext();

    const setConfirmUnload = useCallback(
        (on: boolean) => {
            window.onbeforeunload = on
                ? () => translate("You're still uploading! Are you sure you want to leave this page?")
                : null;
        },
        []
    );

    const getTokenIdFromEntryId = useCallback(
        (entryId: string) => {
            const filteredFilesInfo = filesInfo.filter((fileInfo) => {
                return fileInfo.entry?.id === entryId;
            });
            return filteredFilesInfo.length ? filteredFilesInfo[0].fileMetaData.tokenId : undefined;
        },
        [filesInfo]
    );

    const getUploadFileByTokenId = useCallback(
        (tokenId: string, files: MediaUploadFileInfo[]) => {
            const filteredFilesInfo = files.filter((fileInfo) => {
                return fileInfo.fileMetaData.tokenId === tokenId;
            });
            return filteredFilesInfo.length ? filteredFilesInfo[0] : undefined;
        },
        []
    );

    const getFileNameFromTokenId = useCallback(
        (tokenId: string) => {
            const file = getUploadFileByTokenId(tokenId, filesInfo);
            return file?.file.name;
        },
        [filesInfo, getUploadFileByTokenId]
    );

    const getFileStatusFromTokenId = useCallback(
        (tokenId: string) => {
            const file = getUploadFileByTokenId(tokenId, filesInfo);
            return file?.fileMetaData.status || UploadStatus.CANCELED;
        },
        [filesInfo, getUploadFileByTokenId]
    );

    /**
     * add the entry to the upload-info object and insert it to the upload queue
     */
    const updateFileEntry = useCallback(
        (
            tokenId: string,
            entry: KalturaMediaEntry,
            addToQueue = false,
            entryMetadata?: EntryMetadataStructure,
            isEditable = true
        ) => {
            setFilesInfo((filesInfo: MediaUploadFileInfo[]) => {
                const file = getUploadFileByTokenId(tokenId, filesInfo);
                if (file) {
                    file.entry = entry;
                    file.isEditable = isEditable;

                    if (entryMetadata) {
                        file.entryMetadata = entryMetadata;
                    }
                    if (addToQueue) {
                        file.fileMetaData.status = UploadStatus.IN_QUEUE;
                    }
                }
                return [...filesInfo];
            });
        },
        [setFilesInfo, getUploadFileByTokenId]
    );

    const showPreSaveComponent = useCallback(
        (entryIds: string[], values: any): Promise<{ confirm: boolean; isEditable: boolean }> => {
            return new Promise<{ confirm: boolean; isEditable: boolean }>((resolve) => {
                setFormData({ entryIds, data: values });
                setDisplayPreSaveComponent(true);
                setSaveConfirmationResolvePromise(() => resolve);
            });
        },
        []
    );

    const onBeforeSubmit = async (entryIds: string[], values: any) => {
        if (!preSaveComponent) {
            return { confirm: true, isEditable: true };
        }

        const { confirm, isEditable } = await showPreSaveComponent(entryIds, values);

        return { confirm, isEditable };
    };

    const handleEditResponse = (response: SaveEditFormResponse, isEditable = true) => {
        if (response.error) {
            showToast({ severity: "error", message: translate("Failed to update information") });
        }
        else {
            showToast({ severity: "success", message: translate("Your information has been successfully updated.") });

            response.result.forEach((entryResult) => {
                const relevantTokenId = getTokenIdFromEntryId(entryResult.entry.id);
                if (relevantTokenId) {
                    // update entry object with received data
                    updateFileEntry(relevantTokenId, entryResult.entry, false, entryResult.data, isEditable);
                }
                onSubmit?.(entryResult.entry);
            });

            if (postSaveComponent) {
                setDisplayPostSaveComponent(true);
            }
        }
    };

    const handleBulkEditResponse = (response: SaveEditFormResponse, isEditable = true) => {
        handleEditResponse(response, isEditable);

        if (!response.error) {
            incrementBulkUpdatesCount();
        }
    };

    const incrementBulkUpdatesCount = () => {
        setBulkUpdatesCount((prev) => prev + 1);
    };

    const updateFileStatus = useCallback(
        (tokenId: string, status: UploadStatus) => {
            setFilesInfo((filesInfo: MediaUploadFileInfo[]) => {
                const file = getUploadFileByTokenId(tokenId, filesInfo);
                if (file) {
                    file.fileMetaData.status = status;
                }
                return [...filesInfo];
            });
        },
        [setFilesInfo, getUploadFileByTokenId]
    );

    const removeEntryIdFromStatusCheck = useCallback(
        (tokenId: string) => {
            setFilesInfo((filesInfo) => {
                const relevantUploadFileInfo = filesInfo.filter((fileInfo) => {
                    return fileInfo.fileMetaData.tokenId === tokenId;
                });
                const relevantEntryIdToRemove = relevantUploadFileInfo.length
                    ? relevantUploadFileInfo[0].entry?.id
                    : undefined;
                if (relevantEntryIdToRemove) {
                    console.log("removing entry id from status check: " + relevantEntryIdToRemove);
                    setEntriesIdToCheckStatus((entriesIdToCheckStatus) => {
                        entriesIdToCheckStatus.delete(relevantEntryIdToRemove);
                        return new Set(entriesIdToCheckStatus);
                    });
                }
                return filesInfo;
            });
        },
        [setEntriesIdToCheckStatus]
    );

    const addEntryIdToStatusCheck = useCallback(
        (tokenId: string) => {
            setFilesInfo((filesInfo) => {
                const relevantUploadFileInfo = filesInfo.filter((fileInfo) => {
                    return fileInfo.fileMetaData.tokenId === tokenId;
                });
                const relevantEntryIdToAdd =
                    relevantUploadFileInfo.length && relevantUploadFileInfo[0]
                        ? relevantUploadFileInfo[0].entry?.id
                        : undefined;
                if (relevantEntryIdToAdd) {
                    console.log("adding entry id to status check: " + relevantEntryIdToAdd);
                    setEntriesIdToCheckStatus((entriesIdToCheckStatus) => {
                        entriesIdToCheckStatus.add(relevantEntryIdToAdd);
                        return new Set(entriesIdToCheckStatus);
                    });
                }
                return filesInfo;
            });
        },
        [setEntriesIdToCheckStatus]
    );

    /**
     * notify server to cancel the tokens
     * @params tokenIds ids of tokens to cancel
     */
    const cancelUploads = useCallback(
        (tokenIdsToRemove: string[]) => {
            return uploadServiceRef.current && uploadServiceRef.current.cancelUploads(tokenIdsToRemove);
        },
        []
    );

    const setMessageAccordingToStatus = useCallback(
        (tokenId = "", fileName = "") => {
            let relevantFileName = fileName;
            let relevantFileStatus: UploadStatus;

            if (tokenId) {
                // getFileNameFromTokenId() always finds the file name in this use-case
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                relevantFileName = getFileNameFromTokenId(tokenId)!;
                relevantFileStatus = getFileStatusFromTokenId(tokenId);
            }
            else {
                // tokenId is undefined - failed on token creation
                relevantFileStatus = UploadStatus.UPLOAD_FAILED;
            }

            let messageText = "";
            switch (relevantFileStatus) {
                case UploadStatus.ENTRY_FAILED:
                    messageText = translate("Error processing media: %1", [relevantFileName]);
                    break;
                case UploadStatus.UPLOAD_FAILED:
                    messageText = translate("Upload failed for: %1", [relevantFileName]);
                    break;
                default:
                    break;
            }
            if (messageText !== "") {
                showToast({ severity: "error", message: messageText });
            }
        },
        [getFileNameFromTokenId, getFileStatusFromTokenId, showToast]
    );

    const checkEntriesStatus = useCallback(() => {
        if (entriesIdToCheckStatus.size > 0) {
            checkMediaStatus({ entryIds: Array.from(entriesIdToCheckStatus) }).then(
                (response: { result: KalturaMediaEntry[]; error: boolean }) => {
                    response.result.forEach((entryResult) => {
                        const uploadStatus = StatusesMapper[entryResult.status];
                        const relevantTokenId = getTokenIdFromEntryId(entryResult.id);
                        if (relevantTokenId) {
                            updateFileStatus(relevantTokenId, UploadStatus[uploadStatus]);
                            if (
                                [
                                    UploadStatus.READY,
                                    UploadStatus.ENTRY_FAILED,
                                    UploadStatus.UPLOAD_FAILED,
                                    UploadStatus.CANCELED,
                                ].includes(UploadStatus[uploadStatus])
                            ) {
                                removeEntryIdFromStatusCheck(relevantTokenId);
                            }
                            if (
                                [UploadStatus.ENTRY_FAILED, UploadStatus.UPLOAD_FAILED].includes(
                                    UploadStatus[uploadStatus]
                                )
                            ) {
                                setMessageAccordingToStatus(relevantTokenId);
                            }

                            // ready callback
                            UploadStatus[uploadStatus] === UploadStatus.READY && onEntryReady?.(entryResult);
                        }
                    });
                }
            );
        }
    }, [
        entriesIdToCheckStatus,
        checkMediaStatus,
        getTokenIdFromEntryId,
        updateFileStatus,
        removeEntryIdFromStatusCheck,
        setMessageAccordingToStatus,
    ]);

    const handleUploadEnded = useEventHandler((tokenId: string) => {
        console.log("upload ended for token: " + tokenId);
        updateFileStatus(tokenId, UploadStatus.PROCESSING);
        addEntryIdToStatusCheck(tokenId);
        setFileInfoToUpload(undefined);

        const fileInfo = getUploadFileByTokenId(tokenId, filesInfo);
        if (fileInfo?.entry) {
            onEntryFinishedUploading?.(fileInfo.entry);
        }
    });

    /**
     * initiate the flow for canceling uploads
     * @param tokenIds ids of tokens representing uploads to cancel
     */
    const handleCancelRequested = useEventHandler((tokenIds: string[]) => {
        const entitiesToDelete: { tokenIds: string[]; entryIds: string[] } = { tokenIds: [], entryIds: [] };
        const errors: string[] = [];
        tokenIds.forEach((tokenId) => {
            const fileInfo = getUploadFileByTokenId(tokenId, filesInfo);
            if (!fileInfo) {
                // not really expected to happen, this is just typescript complaining
                return;
            }
            if (![UploadStatus.UPLOADING, UploadStatus.IN_QUEUE].includes(fileInfo.fileMetaData.status)) {
                // upload cannot be cancelled, it is finished
                errors.push(tokenId);
                return;
            }
            const relevantEntryId = fileInfo?.entry?.id;
            if (relevantEntryId) {
                entitiesToDelete.tokenIds.push(tokenId);
                entitiesToDelete.entryIds.push(relevantEntryId);
            }
        });

        // get only tokens that can be cancelled, ie the entry is not done uploading
        const uploadsToCancel = tokenIds.filter((x) => !errors.includes(x));

        if (!uploadsToCancel.length) {
            // no tokens to cancel, we are done here.
            return;
        }

        // call the upload cancelled callback
        onUploadCancelled?.(entitiesToDelete.entryIds);

        // move back to the upload screen on single upload
        if (singleUpload) {
            setFilesInfo([]);
        }

        // cancel tokens
        cancelUploads(uploadsToCancel);
        // delete media where it was already created
        deleteMedia({ entryIds: entitiesToDelete.entryIds }).then((data: Record<string, boolean>) => {
            /*
             * For multi upload, update file statuses to "cancelled".
             * For single upload, the files array is already empty at this point.
             */
            if (!singleUpload) {
                for (const deletedEntryId in data) {
                    const relevantTokenId =
                        entitiesToDelete.tokenIds[entitiesToDelete.entryIds.indexOf(deletedEntryId)];
                    updateFileStatus(relevantTokenId, UploadStatus.CANCELED);
                }
            }
        });
    });

    /**
     * find files valid for cancellation, i.e in status in-queue or uploading
     * @param filesInfo
     */
    const findFilesValidForCancellation = useCallback(
        (filesInfo: MediaUploadFileInfo[]) => {
            return filesInfo.filter(
                (fileInfo) =>
                    fileInfo.fileMetaData.tokenId
                    && [UploadStatus.UPLOADING, UploadStatus.IN_QUEUE].includes(fileInfo.fileMetaData.status)
            );
        },
        []
    );

    /**
     * cancel all uploads in status in-queue or uploading
     */
    const cancelAllValidUploads = useCallback(
        (filesInfo: MediaUploadFileInfo[]) => {
            const files = findFilesValidForCancellation(filesInfo);
            if (!files.length) return;
            // get uploadTokenIds
            const tokenIds = files.map((fileInfo) => fileInfo.fileMetaData.tokenId);

            // cancel:
            handleCancelRequested(tokenIds);
        },
        [findFilesValidForCancellation, handleCancelRequested]
    );

    /**
     * remove a file from the list
     * @param tokenIds   ids of the tokens related to the files to remove
     */
    const removeFilesInfo = useCallback(
        (tokenIds: string[]) => {
            setFilesInfo((prevFilesInfo) => {
                return prevFilesInfo.filter((file) => !tokenIds.includes(file.fileMetaData.tokenId));
            });
        },
        []
    );

    const handleUploadCanceled = useCallback(
        (tokenIds: string[]) => {
            tokenIds.forEach((tokenId) => {
                setFileInfoToUpload((prevFileInfo) => {
                    if (prevFileInfo?.fileMetaData.tokenId === tokenId) {
                        // the currently uploading file was canceled
                        return undefined;
                    }
                    return prevFileInfo;
                });

                updateFileStatus(tokenId, UploadStatus.CANCELED);
            });
        },
        [updateFileStatus]
    );

    // /**
    //  * remove all files in status failed, canceled from the list
    //  * NOTE: this is not YET used.
    //  */
    // const removeAllValidFileInfos = useCallback((filesInfo:UploadFileInfo[])=> {
    //     // find files valid for removal
    //     const filesToRemove = filesInfo.filter((fileInfo) =>
    //         [UploadStatus.UPLOAD_FAILED, UploadStatus.ENTRY_FAILED, UploadStatus.CANCELED].includes(fileInfo.fileMetaData.status));
    //     if (!filesToRemove.length) return;
    //     // remove files.
    //     const tokenIdsToRemove = filesToRemove.map(fileInfo => fileInfo.fileMetaData.tokenId);
    //     removeFilesInfo(tokenIdsToRemove);
    // }, [removeFilesInfo])

    const createMediaEntry = useCallback(
        (tokenId: string, fileName: string) => {
            console.log(`calling createMediaEntry with token ${tokenId} and filename ${fileName}.`);
            const createMediaQuery = {
                name: fileName,
                tokenid: tokenId,
            };
            createMedia(createMediaQuery).then((data: { error: boolean; result?: KalturaMediaEntry }) => {
                console.log(`createMediaEntry returned entry: ${data.result?.id} and error: ${data.error}.`);
                if (data.error || !data.result) {
                    // set the uploadfile status to failed
                    updateFileStatus(tokenId, UploadStatus.UPLOAD_FAILED);
                    setMessageAccordingToStatus(tokenId);
                    // cancel the upload and kill the token
                    cancelUploads([tokenId]);
                    // initiate the next upload - only if the file is currently uploading
                    setFileInfoToUpload((prevFileInfo) => {
                        if (prevFileInfo?.fileMetaData.tokenId === tokenId) {
                            // the failure relates to the currently uploading file
                            return undefined;
                        }
                        return prevFileInfo;
                    });
                }
                else {
                    updateFileEntry(tokenId, data.result, true);
                    // since calls to setState are asynchronous -
                    // we cannot rely on this.state to reflect the new value immediately after calling setState.
                    // to get current state we pass an updater function to setState
                    setFilesInfo((filesInfoState) => {
                        const relevantFileInfo = filesInfoState.find((fileInfo) => {
                            return fileInfo.fileMetaData.tokenId === tokenId;
                        });
                        //add relevant entry to status check only if upload completed
                        if (relevantFileInfo && relevantFileInfo.fileMetaData.status === UploadStatus.PROCESSING) {
                            addEntryIdToStatusCheck(tokenId);
                        }
                        return filesInfoState;
                    });

                    if (data.result?.id) {
                        onEntryCreated?.(data.result);
                    }

                    // clean up created entry on unmount
                    if (cancelRef.current) {
                        console.debug(`createMediaEntry deleting entry: ${data.result?.id}.`);
                        deleteMedia({ entryIds: [data.result?.id] })
                            .then((data: Record<string, boolean>) => {
                                console.debug(`createMediaEntry deleteMedia operation completed:`, data);
                            })
                            .catch((error) => {
                                console.error(
                                    `createMediaEntry Failed to delete media entry: ${data.result?.id}. Error:`,
                                    error
                                );
                            });
                    }
                }
            });
        },
        [
            createMedia,
            updateFileEntry,
            updateFileStatus,
            setMessageAccordingToStatus,
            cancelUploads,
            addEntryIdToStatusCheck,
            onEntryCreated,
        ]
    );

    const updateFileProgress = useCallback(
        (tokenId: string, rate: number) => {
            console.log("reporting progress for token: " + tokenId + ", rate: " + rate);
            setFilesInfo((prevFilesInfo: MediaUploadFileInfo[]) => {
                const file = getUploadFileByTokenId(tokenId, prevFilesInfo);
                if (file) {
                    file.fileMetaData.progress = rate;
                }

                return [...prevFilesInfo];
            });
        },
        [getUploadFileByTokenId]
    );

    const createTokenId = useCallback(
        (fileName: string) => {
            return uploadServiceRef.current?.createTokenId(fileName);
        },
        [uploadServiceRef]
    );

    const isFileAllowed = useCallback(
        (fileName: string) => {
            const fileExtension = fileName.slice(fileName.lastIndexOf("."));
            return filesExts.split(",").includes(fileExtension);
        },
        [filesExts]
    );

    const addToFiles = useCallback(
        (filesToAdd: FileList) => {
            const unsupportedFilesList = [];
            for (let i = 0; i < filesToAdd.length; i++) {
                const fileToUpload = filesToAdd.item(i);
                if (fileToUpload) {
                    if (isFileAllowed(fileToUpload.name)) {
                        createTokenId(fileToUpload.name)?.then(
                            (tokenId) => {
                                console.log("create token returned: " + tokenId);
                                if (tokenId) {
                                    const fileMetaData: FileMetaData = {
                                        tokenId: tokenId,
                                        status: UploadStatus.PRE_QUEUE,
                                    };
                                    const fileInfo: MediaUploadFileInfo = {
                                        file: fileToUpload,
                                        fileMetaData: fileMetaData,
                                    };
                                    setFilesInfo((filesInfo) => {
                                        return [...filesInfo, fileInfo];
                                    });
                                    createMediaEntry(tokenId, fileToUpload.name);
                                }
                                else {
                                    setMessageAccordingToStatus(undefined, fileToUpload.name);
                                }
                            },
                            () => {
                                setMessageAccordingToStatus(undefined, fileToUpload.name);
                            }
                        );
                    }
                    else {
                        unsupportedFilesList.push(fileToUpload.name);
                    }
                }
            }
            if (unsupportedFilesList.length) {
                showToast({
                    severity: "error",
                    message: translate("Unsupported file type: %1", [unsupportedFilesList.join(", ")]),
                });
            }
        },
        [isFileAllowed, createTokenId, createMediaEntry, setMessageAccordingToStatus, showToast]
    );

    const handleDrop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            addToFiles(e.dataTransfer.files);
        },
        [addToFiles]
    );

    const handleSelect = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            if (!e || !e.target || !e.target.files || e.target.files.length === 0) {
                return;
            }
            addToFiles(e.target.files);
        },
        [addToFiles]
    );

    const handleUploadTokenError = useCallback(
        (tokenId: string, errorText?: string) => {
            !!errorText && console.log("error: " + errorText);
            const failedFileName = getFileNameFromTokenId(tokenId);
            setMessageAccordingToStatus(tokenId, failedFileName);
            removeEntryIdFromStatusCheck(tokenId);
            updateFileStatus(tokenId, UploadStatus.UPLOAD_FAILED);
            setFileInfoToUpload(undefined);
        },
        [getFileNameFromTokenId, removeEntryIdFromStatusCheck, updateFileStatus, setMessageAccordingToStatus]
    );

    const handleUploadCancelError = useCallback(
        (tokenIds: string[], errorText?: string) => {
            !!errorText && console.log("error: " + errorText);
            showToast({
                severity: "error",
                message: translate("An unexpected error occurred while trying to cancel uploads"),
            });
        },
        [showToast]
    );

    const uploadNext = useCallback(
        (filesList: MediaUploadFileInfo[]) => {
            /*
        possible flows:
        : if we have more files in the list
        : no more files in the list
        : new files added after upload wasn't going on
         */
            /*
        - if currently upload going on, do nothing
        - if no upload, scan list
            - if got file, initiate next
            - no file, do nothing
         */
            if (fileInfoToUpload) {
                return;
            }
            const fileInfo = filesList.find((file) => file.fileMetaData.status === UploadStatus.IN_QUEUE);
            if (fileInfo) {
                updateFileStatus(fileInfo.fileMetaData.tokenId, UploadStatus.UPLOADING);
                setFileInfoToUpload(fileInfo);
            }
        },
        [fileInfoToUpload, updateFileStatus]
    );

    /**
     *  initiate the check-status interval
     */
    useEffect(() => {
        const intervalId = setInterval(
            () => {
                checkEntriesStatus();
            },
            7000
        );
        return () => {
            clearInterval(intervalId);
        };
    }, [checkEntriesStatus]);

    /**
     * initiate upload when files added to list, or when upload is done/failed
     */
    useEffect(() => {
        uploadNext(filesInfo);
        setConfirmUnload(
            filesInfo.findIndex(
                (file) =>
                    file.fileMetaData.status === UploadStatus.UPLOADING
                    || file.fileMetaData.status === UploadStatus.IN_QUEUE
            ) >= 0
        );
    }, [filesInfo, setConfirmUnload, uploadNext]);

    // keep active uploads for cleanup
    useEffect(() => {
        filesInfoRef.current = filesInfo;
    }, [filesInfo]);

    // clean up active uploads on unmount
    useEffect(() => {
        return () => {
            if (filesInfoRef.current.length > 0) {
                cancelAllValidUploads(filesInfoRef.current);
                setModalData(undefined);
            }
            cancelRef.current = true;
        };
    }, [cancelAllValidUploads]);

    const filesThatNotEditable = filesInfo.filter((file) => file?.isEditable === false);

    const bulkEditEntryIds = filesInfo
        .filter(
            (file) =>
                [UploadStatus.READY, UploadStatus.PROCESSING, UploadStatus.UPLOADING, UploadStatus.IN_QUEUE].includes(
                    file.fileMetaData.status
                ) && file?.isEditable === true
        )
        .map((file) => file.entry?.id)
        .filter((id): id is string => id !== undefined);

    return (
        <StyledPageBox className={`kms-ds-upload-media-wrapper ${className ?? ""}`}>
            {showTitle && (
                <Typography component={"h1"} variant={isSmScreen ? "h2" : "h1"} className={"kms-ds-upload-media-title"}>
                    {translate("Upload Media")}
                </Typography>
            )}
            {showBackButton && (
                <StyledBackLink
                    onClick={(e) => {
                        e.preventDefault();
                        window.history.back();
                    }}
                    width={44}
                    href={""}
                >
                    {translate("Back")}
                </StyledBackLink>
            )}
            <UploadService
                ks={ks}
                serviceUrl={serviceUrl}
                chunkSize={chunkSize}
                maxConcurrentUploadConnections={maxConcurrentUploadConnections}
                fileInfo={fileInfoToUpload}
                onUploadProgress={updateFileProgress}
                onUploadEnded={handleUploadEnded}
                onUploadsCanceled={handleUploadCanceled}
                onUploadError={handleUploadTokenError}
                onCancelError={handleUploadCancelError}
                ref={uploadServiceRef}
            />
            {!singleUpload && <Status files={filesInfo} />}
            {(!singleUpload || filesInfo.length === 0) && (
                <DragAndDrop
                    allowedFilesType={filesExts}
                    fullSize={filesInfo.length === 0}
                    onDrop={handleDrop}
                    onSelect={handleSelect}
                    singleUpload={singleUpload}
                />
            )}
            <FileRows
                files={filesInfo}
                entryIds={bulkEditEntryIds}
                onCancelAll={() => {
                    setModalData({
                        yesCallback: () => {
                            cancelAllValidUploads(filesInfo);
                            setModalData(undefined);
                        },
                        yesButtonLabel: translate("Cancel All"),
                        title: translate("Cancel All Uploads"),
                        text: translate(
                            "Are you sure you want to cancel %1 uploads?<br/>This action cannot be undone.",
                            [findFilesValidForCancellation(filesInfo).length]
                        ),
                    });
                }}
                onEditAll={() => setShowBulkEditModal(true)}
                onCanSaveForm={onCanSaveForm}
                onReadyForPublish={onReadyForPublish}
                onSubmitStarted={onSubmitStarted}
                onSubmit={handleEditResponse}
                onPublishAll={incrementBulkUpdatesCount}
                onCancel={(tokenId: string) => {
                    setModalData({
                        yesCallback: () => {
                            handleCancelRequested([tokenId]);
                            setModalData(undefined);
                        },
                        yesButtonLabel: translate("Cancel Upload"),
                        title: translate("Cancel Upload"),
                        text: translate(
                            "Are you sure you want to cancel this upload?<br/>This action cannot be undone."
                        ),
                    });
                }}
                onRemove={(tokenId: string) => removeFilesInfo([tokenId])}
                editForm={editForm}
                bulkUpdatesCount={bulkUpdatesCount}
                publishEnabled={publishEnabled}
                editFormComponents={editFormComponents}
                formId={formId}
                singleUpload={singleUpload}
                showEditFormButtons={showEditFormButtons}
                showCancelButton={showCancelButton}
                showMediaLink={showMediaLink}
                saveButtonLabel={saveButtonLabel}
                allowSaveUntouched={allowSaveUntouched}
                onBeforeSubmit={onBeforeSubmit}
                bulkFilesIsEditable={filesThatNotEditable.length !== filesInfo.length}
                onEditFormShown={onEditFormShown}
            />
            {!!modalData && (
                <ConfirmationModal
                    noButtonLabel={translate("Back")}
                    noCallback={() => setModalData(undefined)}
                    open={!!modalData}
                    {...modalData}
                />
            )}
            {!singleUpload && (
                <BulkEditModal
                    entryIds={bulkEditEntryIds}
                    fields={editForm}
                    onBeforeSubmit={onBeforeSubmit}
                    onReadyForPublish={onReadyForPublish}
                    onSubmit={handleBulkEditResponse}
                    onCancel={() => setShowBulkEditModal(false)}
                    open={showBulkEditModal}
                    components={editFormComponents}
                />
            )}
            {preSaveComponent && (
                <PreSaveComponent
                    kmsComponent={preSaveComponent}
                    display={displayPreSaveComponent}
                    formData={formData}
                    saveConfirmationResolvePromise={saveConfirmationResolvePromise}
                    setDisplayPreSaveComponent={setDisplayPreSaveComponent}
                />
            )}
            {postSaveComponent && (
                <PostSaveComponent
                    kmsComponent={postSaveComponent}
                    display={displayPostSaveComponent}
                    formData={formData}
                    onClose={() => setDisplayPostSaveComponent(false)}
                />
            )}
        </StyledPageBox>
    );
};
